import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Grid,
  Avatar,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { AlertTriangle, X, ArchiveRestore } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { useRecoveryLogic } from './hooks/useRecoveryLogic';

const RecoveryFileCardContent: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);
  const {
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    recentRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
  } = useRecoveryLogic();

  const handleRecoverClick = () => {
    if (firstRecord) {
      handleRecover(firstRecord);
    }
  };

  const handleDismissClick = (fileId: string) => {
    handleDismiss(fileId);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: 'none',
        backgroundColor: 'transparent',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'scale(1)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'scale(1.02)',
        },
      }}
      onClick={handleRecoverClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Thumbnail Area with Recovery Icon */}
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="div"
            sx={{
              width: '100%',
              aspectRatio: '16/9',
              borderRadius: '8px',
              backgroundColor:
                theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
              border: '2px dashed',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                <ArchiveRestore
                  sx={{
                    position: 'absolute',
                    width: 32,
                    height: 32,
                    color: 'text.secondary',
                    opacity: isHovered ? 0.7 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
                <AlertTriangle
                  sx={{
                    position: 'absolute',
                    width: 32,
                    height: 32,
                    color: 'text.secondary',
                    opacity: isHovered ? 0 : 0.7,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  opacity: 0.8,
                  textAlign: 'center',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                {isHovered
                  ? t('library.recovery.status.finalize')
                  : t('library.recovery.status.interrupted')}
              </Typography>
            </Box>

            {/* Dismiss Button */}
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <Tooltip title={t('library.recovery.dismiss')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (firstRecord) {
                      handleDismissClick(firstRecord.fileId);
                    }
                  }}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(42, 43, 44, 0.9)'
                        : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(4px)',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(42, 43, 44, 1)'
                          : 'rgba(255, 255, 255, 1)',
                    },
                  }}
                >
                  <X sx={{ width: 16, height: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Progress indicator in bottom right */}
            {isRecovering && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  padding: '4px 8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={14} color="inherit" />
                {Math.round(progress)}%
              </Box>
            )}
          </CardMedia>
        </Box>

        {/* Content Area - Matching FileCard structure */}
        <CardContent
          sx={{
            '&:last-child': {
              paddingBottom: '16px',
              paddingLeft: 0,
              paddingRight: 0,
            },
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: 'transparent',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {/* Recovery Avatar - Matching FileCard exactly */}
              <Avatar
                sx={{
                  width: '2rem',
                  height: '2rem',
                  backgroundColor: theme.palette.secondary.main,
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                }}
              >
                !
              </Avatar>

              <Box
                ml={2}
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    width: '100%',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    lineHeight: '1.5em',
                    minHeight: '1.5em',
                    maxHeight: '2.8em',
                    pr: '40px',
                  }}
                >
                  {hasMultiple
                    ? t('library.recovery.title.multiple', {
                        count: recentRecoverableFiles.length,
                      })
                    : t('library.recovery.title.single')}
                </Typography>
                <Box sx={{ pl: '1px' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.6rem',
                      color: theme.palette.text.secondary,
                      opacity: 0.8,
                      mt: '6px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {firstRecord && getTimeAgo(firstRecord.startedAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Box>
    </Card>
  );
};

// Main component that includes Grid wrapper
const RecoveryFileCard: React.FC = () => {
  const { hasRecoverableFiles } = useRecoveryLogic();

  // Don't render if no recent recoverable files
  if (!hasRecoverableFiles) {
    return null;
  }

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
      <RecoveryFileCardContent />
    </Grid>
  );
};

export default RecoveryFileCard;
